import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { openOrderWindow } from '../../../../../store/actions/orders';
import { getEffectiveOrderPayload } from '../../../../../services/utilities/orderUtils';

/**
 * Custom hook to handle order actions (Buy/Sell/Chart) for an instrument.
 * @param {Object} instrument - The raw instrument object
 * @param {boolean} isTradable - Whether the instrument can be traded
 * @param {number} livePrice - The current live price
 * @returns {Object} { handleBuy, handleSell, handleChart }
 */
export const useOrderActions = (instrument, isTradable, livePrice) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userSettings = useSelector((state) => state.profile?.userSettings);
  const positionsArray = useSelector(
    (state) => state.portfolio?.arrPositionsUpdateData
  ) || [];

  // Normalised instrument shape for Redux payload
  const orderInstrument = useMemo(() => ({
    token: Number(instrument.token),
    exchange: instrument.exchange,
    trading_symbol: instrument.trading_symbol,
    display_name: instrument.display_name,
    company_name: instrument.description || '',
    instrument_name: '',
    lot_size: Number(instrument.lotsize) || 1,
    series: '',
    tradable: isTradable,
    price_precision: instrument.price_precision ?? 2,
    ltp: livePrice,
  }), [instrument, isTradable, livePrice]);

  const handleBuy = useCallback((e) => {
    e?.stopPropagation();
    const payload = getEffectiveOrderPayload(orderInstrument, 'BUY', userSettings, positionsArray);
    dispatch(openOrderWindow(payload));
  }, [dispatch, orderInstrument, userSettings, positionsArray]);

  const handleSell = useCallback((e) => {
    e?.stopPropagation();
    const payload = getEffectiveOrderPayload(orderInstrument, 'SELL', userSettings, positionsArray);
    dispatch(openOrderWindow(payload));
  }, [dispatch, orderInstrument, userSettings, positionsArray]);

  const handleChart = useCallback((e) => {
    e?.stopPropagation();
    const precision = instrument.price_precision ?? 2;
    navigate(
      `/overview/tdv/${instrument.exchange}/${instrument.token}/${precision}`,
      { state: { defaultTab: 'chart' } }
    );
  }, [navigate, instrument]);

  return { handleBuy, handleSell, handleChart };
};
